import type {
  RuntimeClientBundle,
  RuntimeFieldState,
  RuntimeRule,
  RuntimeRuleCondition,
  RuntimeValueExpression,
} from './types'

export function evaluateFormState(
  bundle: RuntimeClientBundle,
  values: Record<string, unknown>,
  context: Record<string, unknown> = {},
  providers: Record<string, unknown> = {},
): Record<string, RuntimeFieldState> {
  const state: Record<string, RuntimeFieldState> = {}

  for (const [fieldId, field] of Object.entries(bundle.fields)) {
    state[fieldId] = {
      visible: !field.hidden,
      required: Boolean(field.required),
      readonly: Boolean(field.readonly),
      value: Object.prototype.hasOwnProperty.call(values, fieldId)
        ? values[fieldId]
        : field.default ?? null,
    }
  }

  for (const rule of bundle.rules ?? []) {
    if (rule.enabled === false) continue
    if (!evaluateCondition(rule.when, values, context, providers)) continue

    for (const effect of rule.then ?? []) {
      const target = state[effect.target]
      if (!target) continue

      if (effect.action === 'show') target.visible = true
      else if (effect.action === 'hide') target.visible = false
      else if (effect.action === 'required') {
        target.required = effect.value === null || effect.value === undefined
          ? true
          : Boolean(effect.value)
      } else if (effect.action === 'readonly') {
        target.readonly = effect.value === null || effect.value === undefined
          ? true
          : Boolean(effect.value)
      } else if (effect.action === 'set_value') {
        target.value = effect.value
      }
    }
  }

  return state
}

export function evaluateCondition(
  condition: RuntimeRuleCondition,
  values: Record<string, unknown>,
  context: Record<string, unknown> = {},
  providers: Record<string, unknown> = {},
): boolean {
  if (condition.op === 'and') {
    return (condition.conditions ?? []).every(child => evaluateCondition(child, values, context, providers))
  }
  if (condition.op === 'or') {
    return (condition.conditions ?? []).some(child => evaluateCondition(child, values, context, providers))
  }
  if (condition.op === 'not') {
    const child = condition.conditions?.[0]
    return child ? !evaluateCondition(child, values, context, providers) : false
  }

  const left = condition.left
    ? resolveExpression(condition.left, values, context, providers)
    : undefined

  if (condition.op === 'empty') return isRuleEmpty(left)
  if (condition.op === 'not_empty') return !isRuleEmpty(left)

  const right = condition.right
    ? resolveExpression(condition.right, values, context, providers)
    : undefined

  try {
    if (condition.op === 'eq') return equivalent(left, right)
    if (condition.op === 'neq') return !equivalent(left, right)
    if (condition.op === 'gt') return compare(left, right, (a, b) => a > b)
    if (condition.op === 'gte') return compare(left, right, (a, b) => a >= b)
    if (condition.op === 'lt') return compare(left, right, (a, b) => a < b)
    if (condition.op === 'lte') return compare(left, right, (a, b) => a <= b)
    if (condition.op === 'in') return containsValue(right, left)
    if (condition.op === 'not_in') return !containsValue(right, left)
    if (condition.op === 'contains') return containsValue(left, right)
  } catch {
    return false
  }

  return false
}

export function resolveExpression(
  expression: RuntimeValueExpression,
  values: Record<string, unknown>,
  context: Record<string, unknown> = {},
  providers: Record<string, unknown> = {},
): unknown {
  if (expression.kind === 'value') return expression.value

  const ref = expression.ref ?? ''
  if (ref.startsWith('field.')) return values[ref.slice('field.'.length)]
  if (ref.startsWith('context.')) return resolvePath(context, ref.slice('context.'.length))
  if (ref.startsWith('provider.')) return resolvePath(providers, ref.slice('provider.'.length))
  return undefined
}

export function rulesNeedServerEvaluation(rules: RuntimeRule[]): boolean {
  return rules.some(rule => conditionHasProviderRef(rule.when))
}

function conditionHasProviderRef(condition: RuntimeRuleCondition): boolean {
  if (expressionHasProviderRef(condition.left) || expressionHasProviderRef(condition.right)) return true
  return (condition.conditions ?? []).some(conditionHasProviderRef)
}

function expressionHasProviderRef(expression?: RuntimeValueExpression | null): boolean {
  return expression?.kind === 'ref' && Boolean(expression.ref?.startsWith('provider.'))
}

function resolvePath(data: Record<string, unknown>, path: string): unknown {
  let current: unknown = data
  for (const part of path.split('.')) {
    if (!current || typeof current !== 'object' || Array.isArray(current)) return undefined
    current = (current as Record<string, unknown>)[part]
  }
  return current
}

function isRuleEmpty(value: unknown): boolean {
  return value === null
    || value === undefined
    || value === ''
    || (Array.isArray(value) && value.length === 0)
}

function equivalent(left: unknown, right: unknown): boolean {
  // Python backend uses strict value equality. JSON numbers/strings remain distinct.
  if (Array.isArray(left) || Array.isArray(right) || isPlainObject(left) || isPlainObject(right)) {
    return JSON.stringify(left) === JSON.stringify(right)
  }
  return left === right
}

function compare(
  left: unknown,
  right: unknown,
  operation: (left: number | string, right: number | string) => boolean,
): boolean {
  if (typeof left === 'number' && typeof right === 'number') return operation(left, right)
  if (typeof left === 'string' && typeof right === 'string') return operation(left, right)
  return false
}

function containsValue(container: unknown, needle: unknown): boolean {
  if (Array.isArray(container)) {
    return container.some(item => equivalent(item, needle))
  }
  if (typeof container === 'string' && typeof needle === 'string') {
    return container.includes(needle)
  }
  if (container && typeof container === 'object' && typeof needle === 'string') {
    return Object.prototype.hasOwnProperty.call(container, needle)
  }
  return false
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}
